<template>
  <template v-if="order">
    <template v-if="!order.invoice?.isPaid">
      <section class="mb-8">
        <div
          class="mb-4 rounded-lg border border-error bg-error/10 px-6 py-3 text-center font-semibold text-error"
        >
          Payment Incomplete
        </div>
      </section>
    </template>
    <section class="mb-8">
      <div class="rounded-lg bg-background p-4">
        <dl class="flex justify-between">
          <div>
            <dt class="leading-loose text-background-fg/70">
              Total
            </dt>
            <dd class="text-3xl font-bold">
              {{
                formatToMerchantCurrencyString(
                  order.invoice?.amountGross ??
                    order.totalCost
                )
              }}
            </dd>
          </div>
          <div
            v-if="order.invoice?.amountBalancedGratuity"
            class="flex flex-col justify-end text-sm text-background-fg/60"
          >
            (+{{
              formatToMerchantCurrencyString(
                order.invoice?.amountBalancedGratuity
              )
            }}
            gratuity)
          </div>
        </dl>
        <div class="my-2 h-px w-full bg-background-fg/8" />
        <dl class="space-y-1">
          <div class="flex items-center justify-between">
            <dt
              v-if="order.orderNumber !== null"
              class="text-sm leading-loose text-background-fg/70"
            >
              Order No.
            </dt>
            <dd class="text-base font-semibold">
              #{{ order.orderNumber }}
            </dd>
          </div>

          <div
            v-if="order?.serviceChargeTotal"
            class="flex items-center justify-between"
          >
            <dt
              class="text-sm leading-loose text-background-fg/70"
            >
              Service Charge
            </dt>
            <dd class="text-base font-semibold">
              {{
                formatToMerchantCurrencyString(
                  order.serviceChargeTotal
                )
              }}
            </dd>
          </div>

          <div class="flex items-center justify-between">
            <dt
              class="text-sm leading-loose text-background-fg/70"
            >
              Date
            </dt>
            <dd class="text-base font-semibold">
              {{
                formatToDate(
                  order.invoice?.paidAt ?? order.createdAt
                )
              }}
            </dd>
          </div>
          <div class="flex items-center justify-between">
            <dt
              class="text-sm leading-loose text-background-fg/70"
            >
              Time
            </dt>
            <dd class="text-base font-semibold">
              {{
                formatToTime(
                  order.invoice?.paidAt ?? order.createdAt
                )
              }}
            </dd>
          </div>
          <template v-if="order.cashierId">
            <div class="flex items-center justify-between">
              <dt
                class="text-sm leading-loose text-background-fg/70"
              >
                Cashier
              </dt>
              <dd class="text-base font-semibold">
                {{ cashier?.username }}
              </dd>
            </div>
          </template>

          <template v-if="order.note">
            <div
              class="flex flex-wrap items-center justify-between"
            >
              <dt
                class="text-sm leading-loose text-background-fg/70"
              >
                Note
              </dt>
              <dd class="text-base font-semibold">
                {{ order.note }}
              </dd>
            </div>
          </template>
          <OrderDetailReceipt
            v-if="order.invoice?.isPaid"
            :order-id="orderId"
          />
        </dl>
      </div>
    </section>

    <section class="mb-8">
      <h3 class="mb-3 text-sm text-background-fg/70">
        Payments
      </h3>
      <div class="space-y-2">
        <template
          v-for="transaction in order.payments"
          :key="transaction.id"
        >
          <OrderDetailTransactionItem
            :transaction="transaction"
            @click-transaction="onClickTransaction"
          />
        </template>
      </div>
      <BaseWarningBox
        v-if="hasCurrencyMismatches"
        class="mt-4"
      >
        <p class="mb-1 font-semibold">
          Note on highlighted transactions &amp; refunds:
        </p>
        <p>
          These entries were made in a currency other than
          <strong>{{ merchantCurrency }}</strong
          >. They are recorded in their original currency
          but will <strong>not</strong> appear in any app or
          portal reporting.
        </p>
      </BaseWarningBox>
    </section>

    <section>
      <h3 class="mb-3 text-sm text-background-fg/70">
        Items
      </h3>
      <div class="rounded-lg bg-background p-4">
        <ul class="space-y-2">
          <template
            v-for="orderItem in order.orderItems"
            :key="orderItem.id"
          >
            <OrderDetailLineItem :order-item="orderItem" />
          </template>
        </ul>
      </div>
    </section>
  </template>

  <TransactionPopup
    v-if="currentTransaction"
    :is-open="isTransactionDrawerOpen"
    :transaction="currentTransaction"
    @close="handleCloseTransactionDrawer"
  />
</template>

<script setup lang="ts">
import type {
  OrderDetailPayment,
  OrderDetailRefund,
} from "@/services/orders/retrieveOrder"

const { getById: getCashierById } = useCashiers()
const { formatToMerchantCurrencyString } = useCurrency()

const props = defineProps<{
  orderId: string
}>()

const { data: order } = useOrder(props.orderId)
const { currency: merchantCurrency } = useTenant()

const cashier = computed(
  () => getCashierById(order.value?.cashierId).value
)

const currentTransaction = ref<
  OrderDetailPayment | OrderDetailRefund
>()
const isTransactionDrawerOpen = ref(false)
const showTransactionDrawer = () => {
  isTransactionDrawerOpen.value = true
}
const handleCloseTransactionDrawer = () => {
  isTransactionDrawerOpen.value = false
}

const onClickTransaction = (
  transaction: OrderDetailPayment | OrderDetailRefund
) => {
  if (
    transaction.status === "cancelled" ||
    transaction.status === "pending"
  )
    return

  currentTransaction.value = transaction
  showTransactionDrawer()
}
const hasCurrencyMismatches = computed(() => {
  if (!order.value) return false
  return order.value.payments.some(
    (tx) =>
      tx.currency !== merchantCurrency.value ||
      tx.refunds.some(
        (r) => r.currency !== merchantCurrency.value
      )
  )
})
</script>
